// EIP-712 typed data interfaces and utilities

export interface TypedDataDomain {
  name?: string;
  version?: string;
  chainId?: number | bigint;
  verifyingContract?: `0x${string}`;
  salt?: `0x${string}`;
}

export interface TypedDataField {
  name: string;
  type: string;
}

export interface TypedData {
  domain: TypedDataDomain;
  types: Record<string, TypedDataField[]>;
  primaryType: string;
  message: Record<string, unknown>;
}

/**
 * Comprehensive type guard for EIP-712 typed data
 * Validates the structure and types according to the EIP-712 specification
 */
export function isValidTypedData(data: unknown): data is TypedData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const typedData = data as any;

  // Check domain
  if (!typedData.domain || typeof typedData.domain !== 'object') {
    return false;
  }

  // Check types
  if (!typedData.types || typeof typedData.types !== 'object') {
    return false;
  }

  // Validate types structure
  for (const [, fields] of Object.entries(typedData.types)) {
    if (!Array.isArray(fields)) {
      return false;
    }
    for (const field of fields) {
      if (
        !field ||
        typeof field !== 'object' ||
        typeof field.name !== 'string' ||
        typeof field.type !== 'string'
      ) {
        return false;
      }
    }
  }

  // Check primaryType
  if (
    !typedData.primaryType ||
    typeof typedData.primaryType !== 'string' ||
    !(typedData.primaryType in typedData.types)
  ) {
    return false;
  }

  // Check message
  if (!typedData.message || typeof typedData.message !== 'object') {
    return false;
  }

  return true;
}
